import { type OrderStatus } from '@/features/shop/services/Orders/types'
import { type Address, type ValidAddress } from '@/features/account/services/Addresses/types'
import { formatDate, getDateTimeInfoWithSeconds } from '@/features/shared/utils/date'
import { formatTimeslot } from '@/features/shared/utils/deliveryWindows'

export const formatPhoneNumber = (phoneNumberString: string) => {
  const cleaned = phoneNumberString.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }
  return null
}

export const formatAddress = (address: Address | ValidAddress | undefined) => {
  if (!address) return ''
  const { street1, street2, city, state, zip_code: zipCode } = address

  return `${street1 ? street1 + ', ' : ''}${street2 ? street2 + ', ' : ''}${
    city ? city + ', ' : ''
  }${state ? state + ' ' : ''}${zipCode || ''}`
}

export const formatPrice = (value = 0, includeDollarSign = true) => {
  const roundedValue = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const dollarSign = includeDollarSign ? '$' : ''
  if (value < 0) {
    return `-${dollarSign}${roundedValue}`
  }
  return `${dollarSign}${roundedValue}`
}

export type OrderForFormatting = {
  status: OrderStatus
  delivered_at?: string
  time_start?: string
  time_window: string
  cancelled_at?: string
  early_delivery?: boolean
}
// Eventually we want all delivery times to be displayed in numerical format
// maybe in the future, create a single function that handles all delivery/pickup time formatting
export const formatDeliveryTimeForOrder = (
  order: OrderForFormatting,
  displayInNumericalFormat = false,
  isOrderStatus = false,
) => {
  const { status, cancelled_at, delivered_at: deliveredAt } = order
  const timeWindow = order.time_window

  if (cancelled_at && status === 'cancelled') {
    return displayInNumericalFormat
      ? formatDate(new Date(cancelled_at), 'M/d/yyyy, h:mm aa')
      : formatDate(new Date(cancelled_at), 'MMM d, yyyy, h:mm aa')
  }
  if (deliveredAt && status === 'delivered') {
    return displayInNumericalFormat
      ? formatDate(deliveredAt, 'M/d/yyyy, h:mm aa')
      : formatDate(new Date(deliveredAt), 'MMM d, yyyy, h:mm aa')
  }
  if (!timeWindow) return ''
  if (order.early_delivery) {
    const { day, end } = formatTimeWindow(timeWindow)
    return `${day}, by ${end}`
  }
  if (timeWindow.includes(':')) {
    if (isOrderStatus) {
      return formatDeliveryTimeForOrderStatus(order)
    }
    const { day, start, end } = formatTimeWindow(timeWindow)
    if (displayInNumericalFormat && order.time_start) {
      const date = formatDate(order.time_start, 'M/d/yyyy')
      return `${date}, ${formatTimeslot(start, end)}`
    } else {
      return `${day}, ${formatTimeslot(start, end)}`
    }
  }
  if (timeWindow.includes(',')) {
    return timeWindow
  }
  return `Today, ${timeWindow}`
}

export const formatTimeWindow = (timeWindow: string) => {
  const [day = '', ...rest] = timeWindow.split(':')
  const remainder = rest.join(':')
  const [start = '', end = ''] = remainder.split(' to ')
  const trimClockHours = (time: string) => time.trim().replace(/:00\b/, '')

  return {
    day,
    end: trimClockHours(end),
    start: trimClockHours(start),
  }
}

export const formatDeliveryTimeForOrderStatus = (order: OrderForFormatting) => {
  const { status, time_window: timeWindow = '', delivered_at: deliveredAt = '' } = order
  const { day, end, start } = formatTimeWindow(timeWindow)

  let cancelledAt = ''
  if ('cancelled_at' in order) cancelledAt = order.cancelled_at ?? ''

  switch (status) {
    case 'ready for pickup':
      return formatTimeslot(start, end)
    case 'returned':
      return formatDate(new Date(deliveredAt), 'MMM d, yyyy')
    case 'cancelled':
      return formatDate(new Date(cancelledAt), 'MMM d, yyyy')
    default:
      // We want to preserve casing of dates, but lower case for
      // delivery windows that are today or tomorrow
      const formattedDay = /(today|tomorrow)/i.test(day) ? day.toLowerCase() : day
      return `${formattedDay}, ${formatTimeslot(start, end)}`
  }
}

// returns the first letter of the first and last name in a string
// if only one name is provided, only one initial is returned
export const getInitials = (name: string) => {
  const names = name.split(' ')
  return names.length === 1
    ? names[0]?.charAt(0) || ''
    : (names[0]?.charAt(0) || '') + (names.at(-1)?.charAt(0) || '')
}

// only used for string values with $
export const parseCurrency = (value: string) => {
  if (value && typeof value === 'string') {
    const match = value.match(/[0-9.]/g)
    if (match) {
      return parseFloat(match.join(''))
    }
  }
  return 0
}

export const convertDateToLocale = (date: string) => {
  if (typeof date !== 'string') return ''
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
}

export const formatTimeSaved = (seconds = 0) => {
  const { hours, days, weeks, years } = getDateTimeInfoWithSeconds(seconds)
  if (hours > 9999) {
    if (days > 9999) {
      if (weeks > 9999) {
        return { value: years, text: pluralizer('year', years) }
      } else {
        return { value: weeks, text: pluralizer('week', weeks) }
      }
    } else {
      return { value: days, text: pluralizer('day', days) }
    }
  }
  return { value: hours, text: pluralizer('hour', hours) }
}

// SEO Title Tag Structure: [Product Name] | [White Label]
// e.g. - Sabra Roasted Red Pepper Hummus | Shipt
export const formatProductPageTitle = (name: string, partnerName: string) => {
  if (name) {
    return `${name} | ${partnerName}`
  }
  return partnerName
}

// We need a function to add a period to the end of the promo description
// if one was not added in admin. This was requested by design.
export const addPeriodIfNeeded = (text: string) => {
  if (typeof text !== 'string') return text
  if (text === '') return text
  if (text.slice(-1) !== '.') {
    return text + '.'
  }
  return text
}

// We need a function to remove a period to the end of the promo description
export const removePeriodIfNeeded = (text: string) => {
  if (typeof text !== 'string') return text
  if (text.slice(-1) === '.') {
    return text.substring(0, text.length - 1)
  }
  return text
}

export const toSnakeCase = (string: string) => {
  if (typeof string !== 'string') return string
  return string
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+\d*|\b)|[A-Z]?[a-z]+\d*|[A-Z]|\d+/g)
    ?.map((x) => x.toLowerCase())
    .join('_')
}

export const pluralizer = (word: string, count: number) => (count === 1 ? word : word + 's')

export const removeMeridiem = (time: string) => {
  // expects string with Meridiem ex 7:20 AM returns 7:20
  return time.split(' ')[0] ?? ''
}

export const capitalizeFirstLetter = (string: string) => {
  if (string.length === 0) return string
  return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Format integer cents as a display price. Example: 2500 -> $25, 2599 -> $25.99
 */
export const formatCents = (cents: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100)
